import React, { useState } from 'react';
import { t } from 'i18n';

import Icon from 'Components/Base/Icon';
import QuillViewer from 'Components/Base/Quilljs/QuillViewer';
import DeleteModal from './DeleteModal';
import deletePlaceContactWrapper from './deletePlaceContactWrapper';
import ContactDetailRow from './ContactDetailRow';

const i18nOpts = { scope: 'place_contacts.admin_panel.view' };

export const contactRowTitle = ({ contactType, title }, tProvider, i18nOpts) => {
  if (contactType === 'custom') { return title ?? ''; }
  return tProvider(contactType, i18nOpts);
};

export const sortPlaceContacts = (contacts) => {
  const list = contacts || [];

  const customContacts = list.filter((contact) => contact.contactType === 'custom');
  const nonCustomContacts = list.filter((contact) => contact.contactType !== 'custom');

  const localeCompare = (a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' });
  nonCustomContacts.sort((a, b) => localeCompare(a.contactType, b.contactType));
  customContacts.sort((a, b) => localeCompare(a.title || '', b.title || ''));

  return [...nonCustomContacts, ...customContacts];
};

export const formatVisibility = (visibility, tProvider, i18nOpts) => {
  const MAX_NUMBER_OF_VISIBILITIES = 3;

  const safeVisibility = visibility || {};

  const numberOfVisibilitiesEnabled = Object.values(safeVisibility).filter(Boolean).length;
  if (numberOfVisibilitiesEnabled === 0) return tProvider('not_viewable', i18nOpts);
  if (numberOfVisibilitiesEnabled === MAX_NUMBER_OF_VISIBILITIES) return tProvider('viewable_by_everyone', i18nOpts);

  const healthTeamOnly =
    safeVisibility.yourHealthTeam &&
    !safeVisibility.invitedHealthTeams &&
    !safeVisibility.patientsAndSupportTeams;
  if (healthTeamOnly) {
    return tProvider('viewable_by_health_team', i18nOpts);
  }

  const labelKeyById = {
    patientsAndSupportTeams: 'label_patients_and_support_teams',
    yourHealthTeam: 'label_your_health_team',
    invitedHealthTeams: 'label_invited_health_teams',
  };
  const selected = Object.keys(labelKeyById).filter((k) => safeVisibility[k]);
  const audience = selected.map((k) => tProvider(labelKeyById[k], i18nOpts)).join(' + ');
  return tProvider('viewable_by_audience', { ...i18nOpts, audience });
};

export const normalizePhone = (phone, extension) => {
  const normalizedPhone = phone || '';
  if (!normalizedPhone) return '';
  return extension ? `${normalizedPhone} x${extension}` : normalizedPhone;
};

export const formatAssignedTo = ({ universal, carePlanTemplates }, tProvider, i18nOpts) => {
  if (universal) return tProvider('assigned_all', i18nOpts);

  const templates = (carePlanTemplates || []).map((tpl) => tpl?.name);
  if (templates.length === 0) return tProvider('assigned_zero', i18nOpts);
  return tProvider('assigned_many', { ...i18nOpts, list: templates.join(', ') });
};

const PlaceContactRow = ({ contact, isLast, onDeleteClick }) => {
  const displayTitle = contactRowTitle(contact, t, i18nOpts);
  const phone = normalizePhone(contact?.phone, contact?.extension);

  return (
    <div className={`place_contact_row ${isLast ? 'place_contact_row--last' : ''}`}>
      <div className="header_row">
        <div className="title">{displayTitle}</div>
        <div className="icon_actions">
          <button
            type="button"
            className="icon_button"
            onClick={() => window?.location.assign(`/place_contacts/${contact.id}/edit`)}
            aria-label={t('edit_contact_aria', i18nOpts)}
            title={t('edit_contact_title', i18nOpts)}
          >
            <Icon name="pencil-edit_alt" className="action_icon" />
          </button>
          <button
            type="button"
            className="icon_button"
            onClick={() => onDeleteClick(contact.id)}
            aria-label={t('delete_contact_aria', i18nOpts)}
            title={t('delete_contact_title', i18nOpts)}
          >
            <Icon name="trash" className="action_icon" />
          </button>
        </div>
      </div>

      {phone && (
        <ContactDetailRow
          iconName="phone_alt"
          displayText={phone}
          href={`tel:${contact?.phone}`}
        />
      )}

      {contact?.email && (
        <ContactDetailRow
          iconName="mail_alt2"
          displayText={contact.email}
          href={`mailto:${contact.email}`}
        />
      )}

      {Array.isArray(contact?.instructions) && contact.instructions.length > 0 && (
        <div className="instructions place_contacts_quill_viewer">
          <QuillViewer contentFormatted={contact.instructions} />
        </div>
      )}

      <div className="visibility_row">
        <span className="visibility_item">
          <Icon name="eye" className="visibility_icon" />
          {formatVisibility(contact?.visibility, t, i18nOpts)}
        </span>
        <span className="visibility_divider">|</span>
        <span className="visibility_item">{formatAssignedTo({ universal: contact?.universal, carePlanTemplates: contact?.carePlanTemplates }, t, i18nOpts)}</span>
      </div>
    </div>
  );
};

const View = ({ placeContacts }) => {
  const contacts = sortPlaceContacts(placeContacts);
  const [contactPendingDeleteId, setContactPendingDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(false);

  const closeDeleteDialog = () => {
    if (isDeleting) return;

    setDeleteError(false);
    setContactPendingDeleteId(null);
  };

  const deletePlaceContact = deletePlaceContactWrapper();

  const confirmDelete = () => {
    deletePlaceContact(contactPendingDeleteId, {
      setIsDeleting,
      setDeleteError,
    });
  };

  return (
    <div className="place_contacts_view">
      <div className="card place_contacts_header">
        <div className="header_top">
          <h2 className="heading">{t('page_title', i18nOpts)}</h2>
          <div className="header_action">
            <button
              type="button"
              className="buttonV2"
              onClick={() => window?.location.assign('/place_contacts/new')}
            >
              <span className="desktop_label">{t('add_contacts', i18nOpts)}</span>
              <span className="mobile_label">{t('add_contacts_mobile', i18nOpts)}</span>
            </button>
          </div>
        </div>
        <p className="subheading">
          {t('page_description', i18nOpts)}
        </p>
      </div>

      <div className="card place_contacts_list_card">
        <div className="place_contacts_list">
          {contacts.map((contact, idx) => (
            <PlaceContactRow
              key={`placeContact-${contact.id}`}
              contact={contact}
              isLast={idx === contacts.length - 1}
              onDeleteClick={setContactPendingDeleteId}
            />
          ))}
        </div>
      </div>

      <DeleteModal
        isOpen={!!contactPendingDeleteId}
        isDeleting={isDeleting}
        deleteError={deleteError}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default View;
